import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconSparkles } from "@tabler/icons-react";
import AIPayDraftEditor from "./AIPayDraftEditor";
import { seedReduction } from "./payEditorPieces";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { pinUnlockHeaders } from "../../utils/pinUnlockSession";
import {
  draftToEditorState,
  filledSections,
  nextEditorItemId,
  payExtractionService,
  payDraftPayload,
  withBaseTier,
} from "../../services/payExtraction";

// Read a document uploaded to a rep's profile with AI and turn what it says into
// the rep's pay deal. The AI result is a DRAFT: it prefills the same pay editor
// the offer flow uses, and nothing touches the deal until Save. Mirrors the iOS
// RepDocAIUploadFlow.
//
// Sections the document says nothing about keep the rep's current values, since
// saving replaces the deal's pay wholesale — an offer letter with no expenses
// must not wipe the expenses already on the deal.

const scaleLines = scale =>
  (scale?.scale_lines || []).map(l => ({
    revenue_amount: Number(l.revenue_amount) || 0,
    percentage: Number(l.percentage) || 0,
  }));

// A deal scale's active window (MD/override scales can be limited to a date
// range inside the selling year) → the period input shape, or null for the
// full season.
const scalePeriod = scale =>
  scale && (scale.effective_start_date || scale.effective_end_date)
    ? {
        start_date: scale.effective_start_date ?? null,
        end_date: scale.effective_end_date ?? null,
      }
    : null;

// An existing deal's upfront pay / bonuses / expenses → the input shapes the
// deal endpoints accept. Row ids are dropped: a save replaces the children.
const upfrontInput = up =>
  up
    ? {
        upfront_pay_type: up.upfront_pay_type ?? null,
        fixed_amount: up.fixed_amount ?? null,
        fixed_percentage: up.fixed_percentage ?? null,
        scale_lines: (up.scale_lines || []).map(l => ({
          accounts_amount: l.accounts_amount ?? null,
          amount: l.amount ?? null,
          percentage: l.percentage ?? null,
        })),
      }
    : null;

const bonusInput = b => ({
  name: b.name,
  bonus_type_category: b.bonus_type_category,
  bonus_type_earned_by_item: b.bonus_type_earned_by_item,
  bonus_type_earned_by_type: b.bonus_type_earned_by_type,
  item_name: b.item_name ?? null,
  item_monetary_value: b.item_monetary_value ?? null,
  bonus_amount: b.bonus_amount ?? null,
  upfront_bonus_amount: b.upfront_bonus_amount ?? null,
  bonus_revenue_amount: b.bonus_revenue_amount ?? null,
  bonus_accounts_amount: b.bonus_accounts_amount ?? null,
  bonus_recruits_amount: b.bonus_recruits_amount ?? null,
  recurring_initial_amount: b.recurring_initial_amount ?? null,
  recurring_repeated_amount: b.recurring_repeated_amount ?? null,
  recurring_start_date: b.recurring_start_date ?? null,
  recurring_end_date: b.recurring_end_date ?? null,
  recurring_schedule_type: b.recurring_schedule_type ?? null,
  recurring_day_of_week: b.recurring_day_of_week ?? null,
  recurring_day_of_month: b.recurring_day_of_month ?? null,
  min_agreement_months: b.min_agreement_months ?? null,
});

const expenseInput = e => ({
  name: e.name,
  expense_type_category: e.expense_type_category,
  backend_percentage: e.backend_percentage ?? null,
  backend_fixed_amount: e.backend_fixed_amount ?? null,
  recurring_frontend_amount: e.recurring_frontend_amount ?? null,
  recurring_schedule_type: e.recurring_schedule_type ?? null,
  recurring_day_of_week: e.recurring_day_of_week ?? null,
  recurring_day_of_month: e.recurring_day_of_month ?? null,
});

const dealUserInput = dealUser => ({
  percentage: dealUser.deal_percentage ?? 100,
  commission_scale_lines: scaleLines(dealUser.commission_scale),
  ...(dealUser.upfront_pay
    ? { upfront_pay: upfrontInput(dealUser.upfront_pay) }
    : {}),
  bonuses: (dealUser.default_bonuses || []).map(bonusInput),
});

// The AI draft wins where it found something; the rep's current deal fills the
// rest in.
const seedState = (draft, deal) => {
  const state = draftToEditorState(draft);
  const current = deal?.deal_users?.[0];
  if (state.commissionLines.length === 0) {
    state.commissionLines = scaleLines(current?.commission_scale);
  }
  if (!state.upfront && current?.upfront_pay) {
    state.upfront = upfrontInput(current.upfront_pay);
  }
  if (state.bonuses.length === 0) {
    state.bonuses = (current?.default_bonuses || []).map(b => ({
      _id: nextEditorItemId(),
      ...bonusInput(b),
    }));
  }
  if (state.expenses.length === 0) {
    state.expenses = (deal?.default_expenses || []).map(e => ({
      _id: nextEditorItemId(),
      ...expenseInput(e),
    }));
  }
  // Quality Deductions aren't extracted from documents — carry the deal's own so
  // saving never drops them, and let the manager tune them in the editor.
  if ((state.reductions || []).length === 0) {
    state.reductions = (deal?.reductions || []).map(r =>
      seedReduction(r, nextEditorItemId)
    );
  }
  state.commissionLines = withBaseTier(state.commissionLines);
  // The deal-level MD / override scales aren't part of the AI draft (their
  // lines are carried through on save, see handleSave) — carry their active
  // windows through too so saving never resets them to the full season.
  // payDraftPayload maps these `_` keys to md_scale_period / override_scale_period.
  if (deal?.md_scale) state._mdPeriod = scalePeriod(deal.md_scale);
  if (deal?.override_scale) {
    state._overridePeriod = scalePeriod(deal.override_scale);
  }
  return state;
};

function RepDocAIExtractModal({ opened, onClose, repId, attachment, onSaved }) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [deal, setDeal] = useState(null);
  const [state, setState] = useState(null);
  const [overview, setOverview] = useState(null);
  const [saving, setSaving] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError(null);
    setDraft(null);
    setState(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      // The rep's current deal and the company's pay types are needed either
      // way, and load while the document is being read.
      const dealPromise = fetch(
        getApiEndpoint(`/v1/ps/user-deal/by-rep/${repId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
            // The deal read is PIN-gated. This modal already tolerates a null
            // deal (it just drafts from scratch), so a locked reader still gets
            // the extraction — without the existing terms to compare against.
            ...pinUnlockHeaders(),
          },
        }
      )
        .then(r => (r.ok ? r.json() : null))
        .then(r => r?.data ?? null)
        .catch(() => null);

      const overviewPromise = fetch(getApiEndpoint("/v1/ps/overview"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      })
        .then(r => (r.ok ? r.json() : null))
        .then(r => r?.data ?? null)
        .catch(() => null);

      const extracted = await payExtractionService.extractRepAttachmentPay(
        repId,
        attachment.id,
        jwt
      );
      const [currentDeal, overviewData] = await Promise.all([
        dealPromise,
        overviewPromise,
      ]);

      setDeal(currentDeal);
      setOverview(overviewData);
      setDraft(extracted);
      setState(seedState(extracted, currentDeal));
    } catch (err) {
      setError(err.message || "Couldn't read this document");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT, repId, attachment]);

  useEffect(() => {
    if (opened && attachment) run();
  }, [opened, attachment, run]);

  const handleSave = async () => {
    if (!state) return;
    try {
      setSaving(true);
      setError(null);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const dealId = deal?.deal_id ?? null;
      const dealUsers = deal?.deal_users || [];
      const base = payDraftPayload(state, {
        dealType: deal?.deal_type || "REP",
        percentage: dealUsers[0]?.deal_percentage ?? 100,
      });

      const body = {
        ...base,
        // Splits: the document's pay replaces the first party's, the other
        // parties carry through untouched (the edit endpoint takes pay only,
        // so the party count must match).
        users: [...base.users, ...dealUsers.slice(1).map(dealUserInput)],
      };
      // Recruiting pay isn't extracted from documents — carry whatever the deal
      // already has so saving never drops it.
      if (deal?.md_scale) body.md_scale_lines = scaleLines(deal.md_scale);
      if (deal?.override_scale) {
        body.allow_override = true;
        body.override_scale_lines = scaleLines(deal.override_scale);
      }

      const response = await fetch(
        getApiEndpoint(
          dealId
            ? `/v1/ps/user-deal/${dealId}`
            : `/v1/ps/user-deal/for-rep/${repId}`
        ),
        {
          method: dealId ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(body),
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || `HTTP ${response.status}`);
      }

      // Point each section this document filled in back at it, so the rep's pay
      // view shows where the numbers came from. Best-effort.
      await Promise.all(
        filledSections(draft, state).map(section =>
          payExtractionService.tagPaySectionSource(
            {
              repId,
              section,
              documentType: "UPLOADED",
              documentId: attachment.id,
            },
            jwt
          )
        )
      );

      showSuccess(
        dealId ? "Pay updated from the document" : "Pay deal created"
      );
      if (onSaved) onSaved(payload.data);
      onClose();
    } catch (err) {
      setError(err.message || "Couldn't save the pay deal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={saving ? () => {} : onClose}
      title={
        <Group gap="xs">
          <IconSparkles size={18} color="var(--mantine-color-orange-6)" />
          <Text fw={700}>Read pay from {attachment?.label || "document"}</Text>
        </Group>
      }
      size="xl"
      centered
    >
      <Stack gap="lg">
        {loading && (
          <Center py="xl">
            <Stack gap="sm" align="center">
              <Loader color="orange" />
              <Text size="sm" c="dimmed">
                Reading the document — this can take up to a minute.
              </Text>
            </Stack>
          </Center>
        )}

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        {!loading && state && draft && (
          <>
            <AIPayDraftEditor
              draft={draft}
              state={state}
              onChange={setState}
              overview={overview}
              sellingYearId={deal?.selling_year_id ?? null}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button color="orange" onClick={handleSave} loading={saving}>
                {deal?.deal_id ? "Update pay deal" : "Create pay deal"}
              </Button>
            </Group>
          </>
        )}

        {!loading && !state && error && (
          <Group justify="flex-end">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button color="orange" onClick={run}>
              Try again
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

export default RepDocAIExtractModal;
